import { useCallback, useEffect, useRef, useState } from 'react';
import {
  invitationSchema,
  roleSchema,
  type Invitation,
  type InvitationState,
} from '@forecastify/shared';
import {
  Button,
  Dialog,
  SKELETON_WIDTHS,
  Select,
  Skeleton,
  TextInput,
  skeletonWidth,
} from '@forecastify/ui';
/* ⚠️ The app's own `Field`, not one from `@forecastify/ui` — it wraps a real `<label>` and carries
 * the note and error slots `state-rules.md` asks for. */
import { Field } from '../card';
import { useT, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { useFirstLoad } from '../use-first-load';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Pending invitations, on the people screen. C17.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C14 left a sentence here instead of a list — *"the mockup's list carries pending invitations as
 * people with an `@` avatar. There is no invite table and no send path… The list says where they went
 * rather than showing a row over nothing."* This replaces the sentence.
 *
 * ⚠️⚠️ **A SEPARATE SECTION, NOT ROWS IN THE PEOPLE TABLE.** The mockup mixes invitations into the
 * list as ghost people, which means every column is empty for them — no team, no employment type, no
 * hours — and every filter has to decide what to do with a person who is not one. An invitation is a
 * different kind of thing with three actions of its own, so it gets its own block.
 *
 * ⚠️ **It fetches its own data**, like `use-export` and `use-import`: the people route already makes
 * seven requests and this list has nothing to do with any of them. The people screen stays
 * presentational — it renders this as a node.
 */

export function stateLabel(t: Translate, state: InvitationState): string {
  /* One `t()` per branch with a literal key. */
  if (state === 'pending') return t('invite.state.pending');
  if (state === 'accepted') return t('invite.state.accepted');
  if (state === 'revoked') return t('invite.state.revoked');
  return t('invite.state.expired');
}

export function InvitationsPanel({
  canInvite,
  openSignal,
  showList = true,
}: {
  readonly canInvite: boolean;
  /**
   * A counter the caller bumps to open the invite dialog from outside.
   *
   * ⚠⚠️ **A NONCE RATHER THAN A CONTROLLED `open` BOOLEAN, AND THE DIFFERENCE IS WHO OWNS
   * CLOSING.** The dialog closes itself on submit, on cancel and on Escape; a controlled boolean
   * would make every one of those a round trip through the parent, which then has to remember to
   * clear it or the dialog re-opens on the next render. A counter says only *"open it now"* and
   * leaves the closing where it already works.
   *
   * ⚠️ `undefined` on the first render, so it does not fire on mount.
   */
  readonly openSignal?: number;
  /**
   * ⚠️⚠️ **`false` LEAVES ONLY THE DIALOG, AND ITEM 44 IS WHY THIS PROP EXISTS.** The pending
   * invitations are rows in the people table now — they page, sort and filter with everybody, which
   * is what *"make sure it works across all pages"* required and what merging two arrays in the
   * browser could never do. What this component still owns is the INVITE DIALOG, which the header's
   * *Meghívás* opens through `openSignal` and which has nowhere else to live.
   *
   * ⚠️ **The list is kept rather than deleted** because it is still the right shape for a surface
   * that is not a people table — and deleting a working panel to re-derive it later is how the
   * next screen ends up with a second copy.
   */
  readonly showList?: boolean;
}) {
  const t = useT();
  const session = useSession();

  const [invitations, setInvitations] = useState<readonly Invitation[]>([]);
  /* ⚠️ Only the two fields the select needs. `Role` (the permission-model type) is a different
   * shape from `roleSchema`'s output, and neither belongs in a dropdown's state. */
  const [roles, setRoles] = useState<readonly { id: string; name: string }[]>([]);
  const [open, setOpen] = useState(false);

  /**
   * ⚠⚠️ **IT SKIPS THE FIRST VALUE, AND THE VERSION THAT ONLY CHECKED FOR `undefined` OPENED THE
   * DIALOG ON MOUNT.** The caller's counter starts at `0` — a number, not `undefined` — so the guard
   * never fired and every visit to the people screen landed with the invite dialog already up, its
   * overlay swallowing every click on the page behind it.
   *
   * A ref holding the last value seen is what makes "changed" mean changed. Comparing against
   * `undefined` was asking a different question.
   */
  const lastSignal = useRef(openSignal);
  useEffect(() => {
    if (openSignal === lastSignal.current) return;
    lastSignal.current = openSignal;
    setOpen(true);
    setError(null);
  }, [openSignal]);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const response = await session.request('/workspace/invitations').catch(() => null);
    /* ⚠️ Silent on failure, and only here: a caller without `people.view` gets a 404 by rule 5, and
     * an error banner over the people list about a block they cannot see would be noise. */
    if (response?.ok !== true) return;
    const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
    const parsed = invitationSchema.array().safeParse(body?.data ?? []);
    if (parsed.success) setInvitations(parsed.data);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(load);

  /* ⚠️ The roles are loaded only when the dialog opens. Somebody who never invites anybody should
   * not pay for the permission catalogue on every visit to the people list. */
  useEffect(() => {
    if (!open || roles.length > 0) return;
    void (async () => {
      const response = await session.request('/permissions/roles').catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = roleSchema.array().safeParse(body?.data ?? []);
      if (parsed.success) {
        setRoles(parsed.data.map((role) => ({ id: role.id, name: role.name })));
        setRoleId((current) => (current === '' ? (parsed.data[0]?.id ?? '') : current));
      }
    })();
  }, [open, roles.length, session]);

  const send = async (path: string, init: RequestInit): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const response = await session.request(path, init).catch(() => null);
    setBusy(false);

    if (response === null) {
      setError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      /**
       * ⚠️ **Branching on `code`, never on `message`** — rule 5. Two of these codes are the only
       * refusals a form can act on: one says "they are already here", the other "you already asked".
       */
      const code = body?.error?.code;
      if (code === 'already_member') setError(t('invite.error.alreadyMember'));
      else if (code === 'already_invited') setError(t('invite.error.alreadyInvited'));
      else if (code === 'validation_failed') setError(t('invite.error.email'));
      /**
       * ⚠️⚠️ **A THIRD ACTIONABLE REFUSAL, AND THE ACTION IS NOT "BUY SEATS".** The API's details
       * separate seats in use from seats held by unaccepted invitations precisely because the
       * commonest cause is a stale link somebody never clicked — and the pending list is directly
       * below this form, with a Revoke on every row. Saying *"your plan is full"* would send an OWNER
       * to a sales conversation they may not need.
       */
      else if (code === 'seat_limit_reached') setError(t('invite.error.seatLimit'));
      else setError(t('error.unavailable'));
      return false;
    }
    await load();
    return true;
  };

  const pending = invitations.filter((invitation) => invitation.state === 'pending');

  /** ⚠️ Built once and rendered from BOTH branches — the list is optional, the dialog is not. */
  const dialog = !open ? null : (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) setOpen(false);
      }}
      title={t('invite.dialog.title')}
      description={t('invite.dialog.note')}
      closeLabel={t('common.close')}
      width={460}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('invite.field.email')}>
          <TextInput
            type="email"
            value={email}
            autoComplete="off"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </Field>

        <Field label={t('invite.field.role')}>
          <Select
            value={roleId}
            onChange={(event) => {
              setRoleId(event.target.value);
            }}
            options={roles.map((role) => ({ value: role.id, label: role.name }))}
          />
        </Field>

        {error !== null && (
          <p role="alert" className="text-destructive text-[12px]">
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            disabled={busy || email.trim() === '' || roleId === ''}
            onClick={() => {
              void (async () => {
                const ok = await send('/workspace/invitations', {
                  method: 'POST',
                  body: JSON.stringify({ email: email.trim(), roleId }),
                });
                if (ok) {
                  setOpen(false);
                  setEmail('');
                }
              })();
            }}
          >
            {t('invite.send')}
          </Button>
        </div>
      </div>
    </Dialog>
  );

  /**
   * ⚠️ **Nothing but the dialog when the list has moved.** A `<section>` with a heading and no rows
   * would be an empty block under a table that already shows what it used to.
   */
  if (!showList) return <>{dialog}</>;

  return (
    <section className="border-t px-5 py-4" aria-label={t('invite.heading')}>
      <div className="flex items-center gap-2">
        <h2 className="text-[12.5px] font-medium">{t('invite.heading')}</h2>
        {loading ? null : (
          <span className="text-muted-foreground text-[11.5px]">
            {t('invite.count', { count: pending.length })}
          </span>
        )}
        {/**
         * ⚠⚠️ **NO INVITE BUTTON HERE — IT IS THE HEADER'S, AND TWO WAS ONE TOO MANY.** This
         * panel used to carry its own, which made sense while it was the only one; once the header
         * gained *Meghívás* (`markup.html` 229–232, and where the mockup puts it) the screen had the
         * same action twice, forty rows apart. The one at the bottom is the one nobody scrolls to.
         *
         * ⚠️ **`canInvite` still gates the ROW actions below** — resend and revoke act on one
         * invitation, not on the screen, so they stay where the thing they act on is. The prop is not
         * dead.
         *
         * ⚠️ **`openSignal` is now the only way in**, which is why it exists: the header cannot reach
         * this component's dialog state any other way.
         */}
      </div>

      {error !== null && !open && (
        <p role="alert" className="text-destructive mt-2 text-[12px]">
          {error}
        </p>
      )}

      {loading ? (
        <>
          <ul aria-hidden className="mt-2 flex flex-col divide-y rounded-lg border">
            {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
              <li key={`skeleton-${String(index)}`} className="flex items-center gap-3 px-3 py-2">
                <span className="bg-muted size-7 shrink-0 animate-pulse rounded-full" />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Skeleton width={skeletonWidth(index, 0) / 2} />
                  <Skeleton width={skeletonWidth(index, 1) / 3} className="h-2.5" />
                </span>
              </li>
            ))}
          </ul>
          <div role="status" aria-live="polite" className="sr-only">
            {t('state.loading')}
          </div>
        </>
      ) : invitations.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-[11.5px]">{t('invite.empty')}</p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y rounded-lg border">
          {invitations.map((invitation) => (
            <li key={invitation.id} className="flex items-center gap-3 px-3 py-2">
              <span className="bg-muted text-muted-foreground grid size-7 shrink-0 place-items-center rounded-full text-[11px]">
                {/* ⚠️ The mockup's `@` avatar — the one part of its treatment worth keeping, because
                    it says "not a person yet" at a glance. */}
                @
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px]">{invitation.email}</span>
                <span className="text-muted-foreground block text-[11px]">
                  {invitation.roleName}
                  {invitation.invitedByName === null
                    ? ''
                    : ` · ${t('invite.by', { name: invitation.invitedByName })}`}
                </span>
              </span>
              <span className="text-muted-foreground shrink-0 text-[11.5px]">
                {stateLabel(t, invitation.state)}
              </span>
              {canInvite && invitation.state === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      void send(`/workspace/invitations/${invitation.id}/resend`, {
                        method: 'POST',
                      });
                    }}
                  >
                    {t('invite.resend')}
                  </Button>
                  {/* ⚠️ An OUTLINE button, not a filled destructive one. The design system reserves
                      the filled danger button for irreversible financial deletions; revoking an
                      invitation is neither. */}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      void send(`/workspace/invitations/${invitation.id}`, { method: 'DELETE' });
                    }}
                  >
                    {t('invite.revoke')}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog}
    </section>
  );
}
